import React, { useEffect, useState } from 'react';
import useSWR from 'swr';
import HeaderCard from '../HeaderCard/HeaderCard';
import { IOrderFinances } from 'types/orders';
import { formatRoundedUpNumber } from 'utils/formatNumbers';
import { CURRENCIES } from 'types/bussines';
import { VOLATILE_CURRENCIES } from 'utils/logisticServices.utils';
import CheckOrAddCommentsModal from 'views/orders/Components/CheckOrAddCommentsModal/CheckOrAddCommentsModal';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';
import { getOrderDetailedData, getOrderCommentsByOrderId, getOrderResponsibleUsersList } from 'services/orders';
import { submitComment } from 'views/LogisticServices/utils/general.utils';
import { getOrganizationData } from 'services/bussines';
import { ICommentsAndObservations } from 'types/global';
import UserWhoCancelledOrder from './components/UserWhoCancelledOrder/UserWhoCancelledOrder';
import './OrderCancelledCard.scss';

/**
 * Componente que muestra la información de una orden cancelada
 * @param data - Datos de la orden cancelada
 * @param refreshData - Función opcional para refrescar los datos después de activar una orden
 */
const OrderCancelledCard = ({ data, refreshData }: IOrderCancelledCardProps) => {
  const orderId = data?.orderId;
  const order = data?.order;
  const { PaymentType, organizationId, orderPrice, cancelledBy: userWhoCancelledOrder } = order;
  const paymentType = PaymentType?.name || '';
  const [showCommentsModal, setShowCommentsModal] = useState<boolean>(false);
  const [loadingComments, setLoadingComments] = useState<boolean>(false);
  const [dataComments, setDataComments] = useState<ICommentsAndObservations[]>();

  const { data: responsibleUsersList } = useSWR(
    showCommentsModal && orderId ? [`order-responsible-${orderId}`, orderId] : null,
    () => getOrderResponsibleUsersList(Number(orderId)),
    {
      suspense: false,
      revalidateOnFocus: false,
      onError: () => {
        displayErrorNotification();
      }
    }
  );

  const { data: orderComments } = useSWR(
    showCommentsModal && orderId ? [`order-comments-${orderId}`, orderId] : null,
    () => getOrderCommentsByOrderId(Number(orderId)),
    {
      suspense: false,
      revalidateOnFocus: false,
      onError: () => {
        displayErrorNotification();
      }
    }
  );

  const handleOpenOrCloseCommentsModal = () => setShowCommentsModal(!showCommentsModal);

  const { data: organizationInfo } = useSWR(
    showCommentsModal && organizationId ? [`organization-${organizationId}`, organizationId] : null,
    () => getOrganizationData(Number(organizationId)),
    {
      suspense: false,
      revalidateOnFocus: false,
      onError: () => {
        displayErrorNotification();
      }
    }
  );

  const handleCommentsSubmission = async (comment: ICommentsAndObservations) => {
    if (!orderId) return;

    if (organizationId && !organizationInfo) {
      displayErrorNotification({
        customJSXMessage: <>Cargando información de la organización, por favor intenta de nuevo en un momento</>
      });
      return;
    }

    setLoadingComments(true);
    try {
      const kamUserId = organizationInfo?.assignedKam?.id ? Number(organizationInfo.assignedKam.id) : undefined;
      const { comments, mentions, commentImage } = comment || {};
      await submitComment({
        orderId,
        allowNewCommentsSubmission: !loadingComments,
        handleLoading: setLoadingComments,
        comments: comments,
        mentions: mentions || [],
        commentImage: commentImage,
        ...(kamUserId && { kamUserId: kamUserId })
      });
      const response = await getOrderDetailedData(orderId);
      setDataComments(response?.orderComments || []);
    } catch (error) {
      displayErrorNotification();
    } finally {
      setLoadingComments(false);
    }
  };

  useEffect(() => {
    setDataComments(orderComments);
  }, [orderComments]);

  return (
    <div className="orderCancelledCard">
      <HeaderCard data={data} openModal={handleOpenOrCloseCommentsModal} isCancelled refreshData={refreshData} />
      <div className="containerMain">
        <div className="containerValues">
          <div className="valueRow">
            <span className="valueLabel">Método de pago:</span>
            <span className="valueAmount">{paymentType}</span>
          </div>
          <div className="valueRow">
            <span className="valueLabel">Monto:</span>
            <span className="valueAmount">{`$${
              Object.values(CURRENCIES).some(currency => currency === orderPrice?.currency)
                ? formatRoundedUpNumber(Number(orderPrice?.totalValueUsd || 0), 2)
                : formatRoundedUpNumber(Number(order?.total || 0), 2)
            } ${
              VOLATILE_CURRENCIES.some(currency => currency === orderPrice?.currency)
                ? CURRENCIES.USD
                : orderPrice?.currency
            }`}</span>
          </div>
          {!!userWhoCancelledOrder && (
            <div className="valueRow responsibleUsers">
              <span className="valueLabel">Cancelado por:</span>
              <span className="valueAmount">
                <UserWhoCancelledOrder userWhoCancelledOrder={userWhoCancelledOrder} />
              </span>
            </div>
          )}
        </div>
      </div>
      {showCommentsModal && !!organizationId && (
        <CheckOrAddCommentsModal
          closeModal={handleOpenOrCloseCommentsModal}
          orderComments={dataComments}
          handleSubmitComments={handleCommentsSubmission}
          loading={loadingComments}
          responsibleUsers={responsibleUsersList}
          organizationId={organizationId}
        />
      )}
    </div>
  );
};

interface IOrderCancelledCardProps {
  data: IOrderFinances;
  refreshData?: () => Promise<void>;
}
export default OrderCancelledCard;
